import React from 'react';
import AeroLogo from '../Aero/AeroLogo';
import AeroDotLoader from '../Aero/AeroDotLoader';
import { GoogleGLogo } from '../GoogleSignInButton/GoogleSignInButton';
import GlassButton from '../Aero/GlassButton';
import './AeroSignIn.css';

/**
 * The v3 sign-in look (decided 25 Oct 2026), shared by every Aero auth
 * surface: /login and /signup (UTPluginAuth), the inline sign-in that
 * AeroLayout shows when signed out (AeroAuth), /forgot-password and the Aero
 * branch of /auth/action. Presentation only: each page keeps its own state,
 * handlers and Firebase calls and passes them in as children.
 *
 * Layout: the "Aero." lockup on the hero wash, one centred card.
 */
export default function AeroAuthShell({ children, below = null, className = '' }) {
  return (
    <div className={`aero-signin ${className}`.trim()} data-theme="light">
      <header className="aero-signin-top">
        <a className="aero-signin-logo" href="/" aria-label="Aero home">
          <AeroLogo size="md" />
        </a>
      </header>
      <main className="aero-signin-main">
        <div className="aero-signin-card">{children}</div>
        {below}
      </main>
    </div>
  );
}

/** Title and one line under it. */
export function AeroAuthHeading({ title, children }) {
  return (
    <div className="aero-signin-heading">
      <h1 className="aero-signin-title">{title}</h1>
      {children && <p className="aero-signin-sub">{children}</p>}
    </div>
  );
}

/** Segmented switch between two modes (Sign in / Sign up). */
export function AeroAuthSwitch({ value, options, onChange }) {
  return (
    <div className="aero-signin-seg" role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          className={value === o.value ? 'is-on' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** "Continue with Google" as white glass, with Google's own G mark. */
export function AeroGoogleButton({ onClick, disabled }) {
  return (
    <GlassButton
      fill="glass"
      size="lg"
      block
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="aero-signin-google"
    >
      <GoogleGLogo size={18} className="aero-signin-google-g" />
      Continue with Google
    </GlassButton>
  );
}

export function AeroAuthDivider() {
  return (
    <div className="aero-signin-divider" role="separator" aria-label="or">
      <span>or</span>
    </div>
  );
}

/** A labelled field. Pass the <input> as children. */
export function AeroAuthField({ id, label, aside = null, children }) {
  return (
    <div className="aero-signin-field">
      <div className="aero-signin-label-row">
        <label className="aero-signin-label" htmlFor={id}>{label}</label>
        {aside}
      </div>
      {children}
    </div>
  );
}

export function AeroAuthError({ children }) {
  if (!children) return null;
  return (
    <p className="aero-signin-error" role="alert">
      <span className="aero-signin-error-mark" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

/** A finished or problem state: a mark, a title, a line, then actions. */
export function AeroAuthStatus({ tone = 'done', title, children, actions = null }) {
  return (
    <div className="aero-signin-status">
      <span className={`aero-signin-status-mark is-${tone}`} aria-hidden="true">
        {tone === 'done' ? (
          <svg viewBox="0 0 16 16" width="16" height="16"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        ) : (
          <svg viewBox="0 0 16 16" width="16" height="16"><path d="M8 4v5M8 11.6v.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
        )}
      </span>
      <h1 className="aero-signin-title">{title}</h1>
      {children && <p className="aero-signin-sub">{children}</p>}
      {actions && <div className="aero-signin-status-actions">{actions}</div>}
    </div>
  );
}

/** Waiting: the blue dot and a short line. */
export function AeroAuthWaiting({ children }) {
  return (
    <div className="aero-signin-waiting" role="status">
      <AeroDotLoader size="lg" />
      <p className="aero-signin-sub">{children}</p>
    </div>
  );
}

/** A full-screen veil while a multi-step sign-in finishes (claiming a test). */
export function AeroAuthVeil({ children }) {
  return (
    <div className="aero-signin-veil" role="status" aria-live="polite">
      <div className="aero-signin-veil-card">
        <AeroDotLoader size="lg" />
        <p>{children}</p>
      </div>
    </div>
  );
}

export function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function EyeOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.8 19.8 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.86 19.86 0 0 1-3.17 4.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/** A password input with the show/hide eye. Controlled by the page. */
export function AeroPasswordInput({ id, value, onChange, placeholder, shown, onToggle, autoComplete, autoFocus }) {
  return (
    <div className="aero-signin-password">
      <input
        id={id}
        className="aero-signin-input"
        type={shown ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required
      />
      {onToggle && (
        <button
          type="button"
          className="aero-signin-eye"
          onClick={onToggle}
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          tabIndex={-1}
        >
          {shown ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      )}
    </div>
  );
}
